import { useShallow } from 'zustand/react/shallow'

import { Destination } from '@ui/__generated__/graphql'
import { Button } from '@ui/components/core'
import { RadioButton } from '@ui/components/core/RadioButton'
import { NodeTypeEnum } from '@ui/interfaces'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { useNavigateToNode } from '@ui/lib/hooks/useNavigateToNode'
import { GetDestinationsQuery } from '@ui/loaders/destinations'
import { useNodeAndEdgeStore } from '@ui/stores'
import { useLayoutPanelManagerStore } from '@ui/stores/pipelinesWorkflow'

import { LayoutPanel } from '../PipelineLayoutManager'
import { PanelContainer } from './Containers'
import { CreateDestination } from './CreateDestination'

export const AssignDestination = () => {
  const { data, loading, error, refetch } = GetDestinationsQuery()

  const { updateNodeType, currentNodeId, currentNodeDestinationId, updateNodeKeyValue } = useNodeAndEdgeStore(
    useShallow((state) => ({
      currentNodeId: state.currentNodeId,
      updateNodeType: state.updateNodeType,
      currentNodeDestinationId: state.getCurrentNodeKey('destinationId'),
      updateNodeKeyValue: state.updateNodeKeyValue,
      edges: state.edges,
    }))
  )
  const navigateToNode = useNavigateToNode()

  const { setModalContent, setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setModalContent: state.setModalContent,
      setPanelContent: state.setPanelContent,
    }))
  )

  const logoPathBySlug = useLogoPathBySlug(data?.destinations.map((destination) => destination.template.slug))

  const onDestinationSelect = (destination: Destination) => {
    if (currentNodeId && currentNodeDestinationId !== destination.id) {
      updateNodeType(currentNodeId, NodeTypeEnum.EXPORT)
      updateNodeKeyValue(currentNodeId, {
        destinationId: destination.id,
        modified: true,
        exportType: destination.template.slug || '',
        description: destination.name || '',
        code: destination.template.code || '',
      })
      navigateToNode(currentNodeId)
    }
  }

  const onCreateDestination = async (destination: Destination) => {
    await refetch()
    onDestinationSelect(destination)
  }

  const openModal = () => {
    setModalContent({
      content: (
        <CreateDestination
          onClose={() => setModalContent({ content: null, open: false })}
          onCreate={onCreateDestination}
        />
      ),
      open: true,
    })
  }

  const closePanel = () => {
    setPanelContent({ panel: LayoutPanel.RIGHT_PANEL, content: null, open: false })
  }

  return (
    <PanelContainer
      title="Destinations"
      subtitle="Your pipeline path is currently directing data to this destination. You may change your selection or add a new destination."
      loading={loading}
      error={error}
      onClose={closePanel}>
      <div className="px-4">
        <Button variant="tertiary" className="w-full" size="small" onClick={openModal}>
          Add Destination
        </Button>

        {data?.destinations.map((destination) => (
          <div key={destination.id} className="my-2 rounded-md border border-gray-200 p-4">
            <div className="flex h-7 items-center gap-2">
              <img
                src={logoPathBySlug[destination.template?.slug || '']}
                alt={destination.template?.name || ''}
                className="h-7 w-7"
              />
              <p className="flex-1 text-xs">{destination.name}</p>
              <RadioButton
                variant="secondary"
                onChange={() => onDestinationSelect(destination)}
                checked={currentNodeDestinationId === destination.id}
              />
            </div>
          </div>
        ))}
      </div>
    </PanelContainer>
  )
}
